import { useEffect, useState } from 'react';
import { ArrowRight, Plus, Trash2, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Redirect { id: string; from_path: string; to_url: string; status_code: number; note: string | null; hit_count: number; is_active: number; created_at: number; }

const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm';
const EMPTY = { from_path: '', to_url: '', status_code: '301', note: '' };

export default function Redirects() {
  const [items, setItems] = useState<Redirect[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => { setLoading(true); const res = await apiFetch('/api/admin/redirects'); const d = await res.json(); setItems(d.redirects || []); setLoading(false); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.from_path || !form.to_url) { setError('from_path and to_url are required'); return; }
    setSaving(true); setError('');
    const res = await apiFetch('/api/admin/redirects', { method: 'POST', body: JSON.stringify({ ...form, status_code: Number(form.status_code) }) });
    if (res.ok) { setShowNew(false); setForm({ ...EMPTY }); load(); }
    else { const d = await res.json(); setError(d.error || 'Failed'); }
    setSaving(false);
  };

  const toggle = async (r: Redirect) => { await apiFetch(`/api/admin/redirects/${r.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: r.is_active ? 0 : 1 }) }); load(); };
  const remove = async (id: string) => { if (!confirm('Delete this redirect?')) return; await apiFetch(`/api/admin/redirects/${id}`, { method: 'DELETE' }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-500 to-cyan-600 bg-clip-text text-transparent">Redirect Manager</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">301/302 URL redirects — applied server-side on every request</p>
        </div>
        <button onClick={() => { setShowNew(true); setError(''); }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl text-sm font-medium shadow hover:opacity-90 transition">
          <Plus size={15} /> Add Redirect
        </button>
      </div>

      {showNew && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-teal-200/60 dark:border-teal-700/40 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">New Redirect</h2>
          {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
          {/* Preview */}
          {form.from_path && form.to_url && (
            <div className="flex items-center gap-2 mb-4 text-sm bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-xl px-4 py-2.5 font-mono">
              <span className="text-teal-700 dark:text-teal-300">{form.from_path}</span>
              <ArrowRight size={16} className="text-teal-400" />
              <span className="text-teal-600 dark:text-teal-400">{form.to_url}</span>
              <span className="ml-auto bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-300 px-2 py-0.5 rounded text-xs font-semibold">{form.status_code}</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">From Path *</label><input value={form.from_path} onChange={e => setForm(f => ({ ...f, from_path: e.target.value }))} className={inputCls} placeholder="/old-page/" /></div>
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">To URL *</label><input value={form.to_url} onChange={e => setForm(f => ({ ...f, to_url: e.target.value }))} className={inputCls} placeholder="/new-page/ or https://…" /></div>
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Status Code</label>
              <select value={form.status_code} onChange={e => setForm(f => ({ ...f, status_code: e.target.value }))} className={inputCls}>
                <option value="301">301 — Permanent</option>
                <option value="302">302 — Temporary</option>
                <option value="307">307 — Temporary (preserve method)</option>
                <option value="308">308 — Permanent (preserve method)</option>
              </select>
            </div>
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Note (optional)</label><input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} className={inputCls} placeholder="Why this redirect exists…" /></div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowNew(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button onClick={create} disabled={saving} className="px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 disabled:opacity-50">{saving ? 'Saving…' : 'Create'}</button>
          </div>
        </div>
      )}

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
        {loading ? <div className="py-12 text-center text-gray-400">Loading…</div> : items.length === 0 && !showNew ? (
          <div className="py-16 text-center text-gray-400"><ArrowRight size={40} className="mx-auto mb-3 opacity-30" /><p>No redirects configured. Old URL → new URL, handled server-side automatically.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 dark:bg-gray-700/30">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">From</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">To</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Code</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Hits</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Note</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} className={`border-t border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 ${!r.is_active ? 'opacity-50' : ''}`}>
                  <td className="p-4 font-mono text-xs text-teal-700 dark:text-teal-400">{r.from_path}</td>
                  <td className="p-4 font-mono text-xs text-gray-600 dark:text-gray-400 max-w-[180px] truncate">
                    <a href={r.to_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-teal-600">{r.to_url} <ExternalLink size={11} /></a>
                  </td>
                  <td className="p-4"><span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded text-xs font-mono font-semibold">{r.status_code}</span></td>
                  <td className="p-4 text-gray-600 dark:text-gray-400 font-semibold">{r.hit_count.toLocaleString()}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>{r.is_active ? 'Active' : 'Paused'}</span>
                  </td>
                  <td className="p-4 text-xs text-gray-400 max-w-[120px] truncate">{r.note || '—'}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => toggle(r)} title={r.is_active ? 'Pause' : 'Activate'} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition">{r.is_active ? <ToggleRight size={16} className="text-teal-500" /> : <ToggleLeft size={16} />}</button>
                      <button onClick={() => remove(r.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
